import React, { useState } from "react";
import PropTypes from "prop-types";
import { Divider, Tooltip, Typography, withStyles } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

import ModalSolicitud from "../components/ModalSolicitud";
import { DIAS, describirDias } from "../model/horarios";
import { AZUL_BIBLIOTECA } from "../constants";

// Los dias marcados se ven en el azul del modulo, no en el gris que trae
// ToggleButton.
const BotonDia = withStyles({
  root: {
    "&$selected": {
      backgroundColor: AZUL_BIBLIOTECA,
      color: "#fff",
      "&:hover": { backgroundColor: AZUL_BIBLIOTECA, opacity: 0.85 },
    },
  },
  selected: {},
})(ToggleButton);

/**
 * Quien trabaja en un horario y que dias. Aparecen todas las personas de
 * biblioteca, cada una con los dias de la semana: los marcados son los que
 * trabaja este horario. Marcar o desmarcar un dia se guarda en el momento;
 * sin ningun dia, la persona sale del horario.
 *
 * `onCambiarDias(persona, dias)` resuelve true si el backend acepto; si no,
 * los dias no cambian (por ejemplo, porque se le cruza con otro horario).
 */
const ModalAuxiliaresHorario = ({ horario, personas, onCambiarDias, onCerrar }) => {
  const [pendiente, setPendiente] = useState(null);
  const diasDe = (persona) =>
    horario.auxiliares.find(({ id }) => id === persona.id)?.dias ?? [];

  const cambiar = async (persona, dias) => {
    setPendiente(persona.id);
    await onCambiarDias(persona, [...dias].sort((a, b) => a - b));
    setPendiente(null);
  };

  return (
    <ModalSolicitud
      titulo={`¡ Auxiliares de ${horario.hora_entrada} a ${horario.hora_salida} !`}
      ancho="sm"
      onCerrar={onCerrar}
    >
      {personas.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          Nadie tiene perfil de biblioteca. Asigna ADMINISTRADOR BIBLIOTECA o
          AUXILIAR BIBLIOTECA desde Personas.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Marca los días en que cada persona trabaja este horario.
          </Typography>
          {personas.map((persona, i) => {
            const dias = diasDe(persona);
            return (
              <div key={persona.id}>
                {i > 0 && <Divider />}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "10px 0",
                  }}
                >
                  <div>
                    <Typography variant="body2">{persona.nombre}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {dias.length ? describirDias(dias) : "No está en este horario"}
                    </Typography>
                  </div>
                  <ToggleButtonGroup
                    size="small"
                    value={dias}
                    onChange={(evento, elegidos) => cambiar(persona, elegidos)}
                  >
                    {DIAS.map(({ valor, corto, nombre }) => (
                      <BotonDia
                        key={valor}
                        value={valor}
                        aria-label={nombre}
                        disabled={pendiente !== null}
                      >
                        <Tooltip title={nombre}>
                          <span style={{ minWidth: 14 }}>{corto}</span>
                        </Tooltip>
                      </BotonDia>
                    ))}
                  </ToggleButtonGroup>
                </div>
              </div>
            );
          })}
        </>
      )}
    </ModalSolicitud>
  );
};

ModalAuxiliaresHorario.propTypes = {
  horario: PropTypes.shape({
    hora_entrada: PropTypes.string.isRequired,
    hora_salida: PropTypes.string.isRequired,
    auxiliares: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, dias: PropTypes.arrayOf(PropTypes.number) }),
    ).isRequired,
  }).isRequired,
  personas: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, nombre: PropTypes.string }),
  ).isRequired,
  onCambiarDias: PropTypes.func.isRequired,
  onCerrar: PropTypes.func.isRequired,
};

export default ModalAuxiliaresHorario;
